import { useState } from 'react';

import type { Snippet } from '../../../../../shared/snippets';

export type SnippetSelection = Set<string> | 'all';

export type SnippetSelectionState = {
  /**
   * The current selection state - either a Set of snippet IDs or 'all' for selecting all snippets.
   */
  selection: SnippetSelection;
  /**
   * Clears all selections, resetting to an empty Set.
   */
  clearSelection: () => void;
  /**
   * Selects all snippets across all pages by setting selection to 'all'.
   */
  selectAll: () => void;

  /**
   * Returns true when all current page snippets are selected (either 'all' or all page snippets in Set).
   */
  isHeaderChecked: () => boolean;
  /**
   * Returns true when some but not all current page snippets are selected (partial selection state).
   */
  isHeaderIndeterminate: () => boolean;
  /**
   * Toggles between selecting all current page snippets or clearing selection based on current state.
   */
  toggleHeader: () => void;

  /**
   * Checks if a specific snippet is selected by its ID.
   */
  isSnippetSelected: (snippetId: string) => boolean;
  /**
   * Toggles the selection state of a specific snippet.
   */
  toggleSnippet: (snippetId: string) => void;
};

const useSnippetSelectionState = (currentPage: Snippet[]): SnippetSelectionState => {
  const [selection, setSelection] = useState<SnippetSelection>(new Set());

  const clearSelection = () => {
    setSelection(new Set());
  };

  const selectAll = () => {
    setSelection('all');
  };

  const isHeaderChecked = (): boolean => {
    return selection === 'all' || (selection.size > 0 && selection.size === currentPage.length);
  };

  const isHeaderIndeterminate = (): boolean => {
    return selection !== 'all' && selection.size > 0 && selection.size < currentPage.length;
  };

  const toggleHeader = () => {
    if (isHeaderChecked() || isHeaderIndeterminate()) {
      // If any snippets are selected, deselect all.
      clearSelection();
    } else {
      // If no snippets are selected, select all on the current page.
      setSelection(new Set(currentPage.map(snippet => snippet.id)));
    }
  };

  const isSnippetSelected = (snippetId: string): boolean => {
    if (selection === 'all') {
      return true;
    }

    return selection.has(snippetId);
  };

  const toggleSnippet = (snippetId: string) => {
    const isSelected = isSnippetSelected(snippetId);
    if (selection === 'all') {
      // If all snippets are selected and one is clicked, select all but this one.
      const newSelection = new Set(currentPage.map((pageSnippet) => pageSnippet.id));
      newSelection.delete(snippetId);
      setSelection(newSelection);
    } else if (isSelected) {
      // If the snippet is already selected, remove it from the selection.
      const newSelection = new Set(selection);
      newSelection.delete(snippetId);
      setSelection(newSelection);
    } else {
      // If the snippet is not selected, add it to the selection.
      const newSelection = new Set(selection);
      newSelection.add(snippetId);
      setSelection(newSelection);
    }
  };

  return {
    selection,
    clearSelection,
    selectAll,

    isHeaderChecked,
    isHeaderIndeterminate,
    toggleHeader,

    isSnippetSelected,
    toggleSnippet,
  };
};

export default useSnippetSelectionState;
